/**
 * taskGroupsAdmin.service.ts — Admin > Task Groups: the account's task groups
 * (task_groups) and what they are made of, through the API v5 calls the
 * corporate dashboard's Task Groups page uses:
 *   - /v5/task-groups (list and detail carry the items and triggers; delete =
 *     active 0, restore = update with active 1);
 *   - the triggers: Add At / Remove At rows per plan stage
 *     (/task-group-stage-triggers), plan status (/task-group-status-triggers)
 *     and appointment stage (/task-group-appt-triggers);
 *   - the task items (/task-items): which task, when, how often, who does it;
 *   - /task-group-filters: every list the editor picks from.
 *
 * `saveTaskGroup` writes a whole edit at Save: the group, then only the
 * triggers and items that changed (corp deletes and re-adds every trigger on
 * each save, and writes items the moment they are added or removed). A group's
 * filters are comma-separated ids, '' for none. Writes need Admin Access.
 */
import { apiClient, getErrorMessage } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

// ─── Labels ────────────────────────────────────────────────────────────────────

export const TRIGGER_TYPE_LABELS: Record<number, string> = {
  0: 'Plan Stage',
  1: 'Appointment Stage',
  2: 'Patient Entry',
  3: 'Lab Results',
  4: 'Payment Entry',
  5: 'Plan Status',
};

/** Corp's list badge colours per trigger. */
export const TRIGGER_TYPE_BADGE: Record<number, string> = {
  0: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  1: 'bg-amber-50 text-amber-700 border-amber-200',
  2: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  3: 'bg-pink-50 text-pink-700 border-pink-200',
  4: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  5: 'bg-violet-50 text-violet-700 border-violet-200',
};

/** The Trigger Type picker's order (corp's), with its dot colours. */
export const TRIGGER_TYPE_OPTIONS = [
  { value: 0, dot: 'bg-indigo-500' },
  { value: 5, dot: 'bg-violet-500' },
  { value: 1, dot: 'bg-amber-500' },
  { value: 2, dot: 'bg-emerald-500' },
  { value: 4, dot: 'bg-cyan-500' },
  { value: 3, dot: 'bg-pink-500' },
] as const;

/** Triggers with no Add At list: they fire on an event. */
export const EVENT_TRIGGERS = new Set([2, 3, 4]);

/** Triggers that pick which plan the tasks go on (Apply To). */
export const APPLY_TO_TRIGGERS = new Set([1, 3, 4]);

export const APPT_STATUS_LABELS: Record<number, string> = {
  1: 'Requested',
  2: 'Scheduled',
  3: 'Confirmed',
  4: 'Checked In',
  5: 'Checked Back',
  6: 'Checked Out',
  7: 'Cancelled',
  8: 'No Show',
  9: 'Wait List',
  10: 'Contact Attempted',
};

export const DATE_TYPE_LABELS: Record<number, string> = {
  1: 'Assignment Date',
  9: 'Next Appointment',
  3: 'Next Consult',
  4: 'Next Pre-Op',
  5: 'Next Surgery',
  6: 'Next Follow-Up',
  7: 'Next Non-Surgical',
  8: 'Next Birthday',
  2: 'Other Date',
};

/** Corp lists the date types in this order. */
export const DATE_TYPE_ORDER = [1, 9, 3, 4, 5, 6, 7, 8, 2] as const;

export const TIME_TYPE_LABELS: Record<number, string> = { 1: 'Days', 2: 'Weeks', 3: 'Months', 4: 'Years' };

export const PRIORITY_LABELS: Record<number, string> = { 1: 'Low', 2: 'Medium', 3: 'High' };
export const PRIORITY_DOT: Record<number, string> = { 1: 'bg-green-500', 2: 'bg-yellow-500', 3: 'bg-red-500' };

/** Assigned To choices; the plan roles take the account's own role titles. */
export const ASSIGNED_TO_OPTIONS = [
  { value: 'coord', label: 'Coordinator From Plan', roleType: 'Coordinator' },
  { value: 'nurse', label: 'Nurse From Plan', roleType: 'Nurse' },
  { value: 'doc', label: 'Medical Provider From Plan', roleType: 'Doctor' },
  { value: 'rec', label: 'Initial Contact From Plan', roleType: 'Receptionist' },
  { value: 'role', label: 'User Group' },
  { value: 'user', label: 'User' },
  { value: 'ftp', label: 'Automatic' },
] as const;

/** The group filters, in corp's order; roles and the facility label are worded per account / trigger. */
export const FILTER_KEYS = [
  'planTypeID',
  'doctorID',
  'coordID',
  'nurseID',
  'facilityID',
  'procedureID',
  'productID',
  'membershipID',
  'packageID',
  'leadSourceID',
  'languageID',
  'appointmentTypeID',
  'appointmentTypeGroupID',
  'currentStatus',
  'appointmentFacilityID',
  'tagID',
  'extraFilters',
] as const;

export type FilterKey = (typeof FILTER_KEYS)[number];

/** Which /task-group-filters list each filter picks from. */
export const FILTER_OPTION_KEY: Record<FilterKey, keyof TaskGroupFilterOptions | null> = {
  planTypeID: 'planTypes',
  doctorID: 'doctors',
  coordID: 'coordinators',
  nurseID: 'nurses',
  facilityID: 'facilities',
  procedureID: 'procedures',
  productID: 'products',
  membershipID: 'memberships',
  packageID: 'packages',
  leadSourceID: 'leadSources',
  languageID: 'languages',
  appointmentTypeID: 'appointmentTypes',
  appointmentTypeGroupID: 'appointmentTypeGroups',
  currentStatus: 'appointmentStatuses',
  appointmentFacilityID: 'facilities',
  tagID: 'patientTags',
  extraFilters: null,
};

/** "Other": the one extra filter legacy offers. */
export const EXTRA_FILTER_OPTIONS: FilterOption[] = [{ id: '1', name: '12 months since Medical History update' }];

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface TaskItem {
  /** 0 until saved. */
  taskItemID: number;
  taskID: number;
  /** From the API's task relation, for a task this list no longer offers. */
  taskName: string;
  taskAutoID: number;
  timeType: number;
  /** Negative = before the date. */
  timeAmount: number;
  recurringType: number;
  recurringAmount: number;
  dateType: number;
  /** coord / nurse / doc / rec / ftp / user, or role_<id> (all members) / group_<id> (one member). */
  assignedToID: string;
  priority: number;
  recurring: boolean;
  recurringNumber: number;
  /** "YYYY-MM-DD" for "Other Date", else ''. */
  customDate: string;
  /** The user, when assigned to User. */
  sendToID: number;
}

export interface GroupTrigger {
  id: number;
  /** Stage id, status (stage sub) id or appointment status. */
  value: number;
  remove: boolean;
  /** The API's own name for it, when it sends one. */
  label: string;
}

export interface AdminTaskGroup {
  taskGroupID: number;
  name: string;
  active: boolean;
  /** "Complete Before Due": true = Not Allowed. */
  required: boolean;
  triggerType: number;
  applyToPlan: boolean;
  firstAppointmentOnly: boolean;
  filters: Record<FilterKey, string>;
  items: TaskItem[];
  stageTriggers: GroupTrigger[];
  statusTriggers: GroupTrigger[];
  apptTriggers: GroupTrigger[];
}

export interface FilterOption {
  id: string;
  name: string;
  /** Appointment types carry their group. */
  category?: string;
}

export interface TaskGroupFilterOptions {
  stages: FilterOption[];
  stageSubs: FilterOption[];
  planTypes: FilterOption[];
  doctors: FilterOption[];
  coordinators: FilterOption[];
  nurses: FilterOption[];
  facilities: FilterOption[];
  procedures: FilterOption[];
  leadSources: FilterOption[];
  languages: FilterOption[];
  appointmentTypes: FilterOption[];
  appointmentTypeGroups: FilterOption[];
  appointmentStatuses: FilterOption[];
  patientTags: FilterOption[];
  products: FilterOption[];
  memberships: FilterOption[];
  packages: FilterOption[];
  roles: FilterOption[];
}

export interface AccountUser {
  userID: number;
  name: string;
}

// ─── Reading ───────────────────────────────────────────────────────────────────

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function flag(v: unknown): boolean {
  return v === true || v === 1 || v === '1' || v === 'true';
}

function rows(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (data && typeof data === 'object') return Object.values(data as Record<string, unknown>) as Record<string, unknown>[];
  return [];
}

/** A stored filter: '' for none (the API also stores '0' or 0 for none). */
export function csvValue(v: unknown): string {
  if (v == null || v === 0 || v === '0') return '';
  return String(v)
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '' && s !== '0')
    .join(',');
}

export function csvIds(v: string): string[] {
  return v === '' ? [] : v.split(',');
}

function toItem(row: Record<string, unknown>): TaskItem {
  const task = (row.task ?? {}) as Record<string, unknown>;
  const customDate = String(row.customDate ?? '');
  return {
    taskItemID: num(row.taskItemID),
    taskID: num(row.taskID),
    taskName: String(task.name ?? ''),
    taskAutoID: num(task.autoTaskID ?? row.autoTaskID),
    timeType: num(row.timeType) || 1,
    timeAmount: num(row.timeAmount),
    recurringType: num(row.recurringType) || 1,
    recurringAmount: num(row.recurringAmount) || 1,
    dateType: num(row.dateType) || 1,
    assignedToID: String(row.assignedToID ?? '') || 'coord',
    priority: num(row.priority) || 2,
    recurring: flag(row.recurring),
    recurringNumber: num(row.recurringNumber) || 2,
    customDate: /^\d{4}-\d{2}-\d{2}/.test(customDate) && !customDate.startsWith('0000') ? customDate.slice(0, 10) : '',
    sendToID: num(row.sendToID),
  };
}

export function toAdminTaskGroup(row: Record<string, unknown>): AdminTaskGroup {
  const filters = Object.fromEntries(FILTER_KEYS.map((k) => [k, csvValue(row[k])])) as Record<FilterKey, string>;
  return {
    taskGroupID: num(row.taskGroupID),
    name: String(row.name ?? ''),
    active: flag(row.active),
    required: flag(row.required),
    triggerType: num(row.triggerType),
    applyToPlan: flag(row.applyToPlan),
    firstAppointmentOnly: flag(row.firstAppointmentOnly),
    filters,
    items: rows(row.task_items).map(toItem),
    stageTriggers: rows(row.stage_triggers).map((t) => ({
      id: num(t.taskGroupsStageTriggerID),
      value: num(t.stageID),
      remove: flag(t.remove),
      label: String((t.stage as Record<string, unknown> | undefined)?.name ?? ''),
    })),
    statusTriggers: rows(row.status_triggers).map((t) => {
      const sub = (t.stage_sub ?? {}) as Record<string, unknown>;
      const parent = String((sub.custom_stage as Record<string, unknown> | undefined)?.stageName ?? '');
      const name = String(sub.name ?? '');
      return { id: num(t.taskGroupsStatusTriggerID), value: num(t.stageSubID), remove: flag(t.remove), label: name && parent ? `${parent} > ${name}` : name };
    }),
    apptTriggers: rows(row.appt_triggers).map((t) => ({
      id: num(t.taskGroupsApptTriggerID),
      value: num(t.apptStatus),
      remove: flag(t.remove),
      label: APPT_STATUS_LABELS[num(t.apptStatus)] ?? '',
    })),
  };
}

/** Every task group, deleted ones too, with items and triggers. */
export function listAdminTaskGroups(): Promise<AdminTaskGroup[]> {
  return apiClient.get<ApiResponse<unknown>>('/task-groups').then((r) => rows(r.data?.data).map(toAdminTaskGroup));
}

export function getAdminTaskGroup(id: number): Promise<AdminTaskGroup> {
  return apiClient.get<ApiResponse<Record<string, unknown>>>(`/task-groups/${id}`).then((r) => toAdminTaskGroup(r.data?.data ?? {}));
}

export function getTaskGroupFilterOptions(): Promise<TaskGroupFilterOptions> {
  return apiClient.get<ApiResponse<Record<string, unknown>>>('/task-group-filters', { params: { type: 'all' } }).then((r) => {
    const data = r.data?.data ?? {};
    const list = (key: string): FilterOption[] =>
      rows(data[key])
        .map((o) => ({
          id: String(o.id ?? o.value ?? ''),
          name: String(o.name ?? o.title ?? ''),
          ...(o.category ? { category: String(o.category) } : {}),
        }))
        .filter((o) => o.id !== '');
    return {
      stages: list('stages'),
      stageSubs: list('stageSubs'),
      planTypes: list('planTypes'),
      doctors: list('doctors'),
      coordinators: list('coordinators'),
      nurses: list('nurses'),
      facilities: list('facilities'),
      procedures: list('procedures'),
      leadSources: list('leadSources'),
      languages: list('languages'),
      appointmentTypes: list('appointmentTypes'),
      appointmentTypeGroups: list('appointmentTypeGroups'),
      appointmentStatuses: list('appointmentStatuses'),
      patientTags: list('patientTags'),
      products: list('products'),
      memberships: list('memberships'),
      packages: list('packages'),
      roles: list('roles'),
    };
  });
}

/** Active users, "Last, First". */
export function listAccountUsers(): Promise<AccountUser[]> {
  return apiClient.get<ApiResponse<Record<string, unknown>[]>>('/account-users').then((r) =>
    rows(r.data?.data)
      .filter((u) => flag(u.active))
      .map((u) => ({ userID: num(u.userID), name: `${String(u.lastName ?? '')}, ${String(u.firstName ?? '')}` }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  );
}

// ─── Writing ───────────────────────────────────────────────────────────────────

export function deleteTaskGroup(id: number): Promise<void> {
  return apiClient.delete(`/task-groups/${id}`).then(() => undefined);
}

export function restoreTaskGroup(id: number): Promise<void> {
  return apiClient.put(`/task-groups/${id}`, { active: true }).then(() => undefined);
}

/** What the editor saves. Add At lists are kept for every trigger type, but only the chosen type's is written. */
export interface TaskGroupDraft {
  name: string;
  required: boolean;
  triggerType: number;
  applyToPlan: boolean;
  firstAppointmentOnly: boolean;
  addAtStages: string[];
  addAtStatuses: string[];
  addAtApptStages: string[];
  removeAtStages: string[];
  removeAtStatuses: string[];
  removeAtApptStages: string[];
  filters: Record<FilterKey, string>;
  items: TaskItem[];
}

type TriggerKind = 'stage' | 'status' | 'appt';

const TRIGGER_PATH: Record<TriggerKind, string> = {
  stage: '/task-group-stage-triggers',
  status: '/task-group-status-triggers',
  appt: '/task-group-appt-triggers',
};

const TRIGGER_FIELD: Record<TriggerKind, string> = { stage: 'stageID', status: 'stageSubID', appt: 'apptStatus' };

function wantedTriggers(d: TaskGroupDraft): { kind: TriggerKind; value: number; remove: boolean }[] {
  const out: { kind: TriggerKind; value: number; remove: boolean }[] = [];
  const add = (kind: TriggerKind, ids: string[], remove: boolean) => {
    for (const id of new Set(ids)) if (Number(id)) out.push({ kind, value: Number(id), remove });
  };
  if (d.triggerType === 0) add('stage', d.addAtStages, false);
  if (d.triggerType === 5) add('status', d.addAtStatuses, false);
  if (d.triggerType === 1) add('appt', d.addAtApptStages, false);
  add('stage', d.removeAtStages, true);
  add('status', d.removeAtStatuses, true);
  add('appt', d.removeAtApptStages, true);
  return out;
}

const ITEM_FIELDS = [
  'taskID',
  'timeType',
  'timeAmount',
  'recurringType',
  'recurringAmount',
  'dateType',
  'assignedToID',
  'priority',
  'recurring',
  'recurringNumber',
  'customDate',
  'sendToID',
] as const;

/** The fields an item is saved with (the API fills the task's own action columns). */
export function itemPayload(item: TaskItem): Record<string, unknown> {
  const out: Record<string, unknown> = Object.fromEntries(ITEM_FIELDS.map((f) => [f, item[f]]));
  if (item.dateType !== 2 || item.customDate === '') delete out.customDate;
  return out;
}

export function itemChanged(a: TaskItem, b: TaskItem): boolean {
  return ITEM_FIELDS.some((f) => String(a[f]) !== String(b[f]));
}

export function groupPayload(d: TaskGroupDraft): Record<string, unknown> {
  return {
    name: d.name.trim(),
    triggerType: d.triggerType,
    required: d.required,
    applyToPlan: APPLY_TO_TRIGGERS.has(d.triggerType) ? d.applyToPlan : false,
    firstAppointmentOnly: d.triggerType === 1 ? d.firstAppointmentOnly : false,
    ...d.filters,
  };
}

export interface SaveTaskGroupResult {
  taskGroupID: number;
  /** The trigger / item writes that failed (the group itself saved). */
  failures: string[];
}

/**
 * Save the group, then its triggers and items: delete what was taken out,
 * add what is new, update the items that changed. The group write must
 * succeed (it throws); the rest are collected as failures.
 */
export async function saveTaskGroup(original: AdminTaskGroup | null, d: TaskGroupDraft): Promise<SaveTaskGroupResult> {
  let taskGroupID: number;
  if (original) {
    taskGroupID = original.taskGroupID;
    await apiClient.put(`/task-groups/${taskGroupID}`, groupPayload(d));
  } else {
    const r = await apiClient.post<ApiResponse<Record<string, unknown>>>('/task-groups', { ...groupPayload(d), active: true });
    taskGroupID = num(r.data?.data?.taskGroupID);
    if (!taskGroupID) throw new Error('The task group was not created.');
  }

  const writes: Promise<unknown>[] = [];

  // Triggers: keep the ones still wanted, delete the rest (and duplicates), add the new ones.
  const wanted = wantedTriggers(d);
  const existing: { kind: TriggerKind; t: GroupTrigger }[] = original
    ? [
        ...original.stageTriggers.map((t) => ({ kind: 'stage' as const, t })),
        ...original.statusTriggers.map((t) => ({ kind: 'status' as const, t })),
        ...original.apptTriggers.map((t) => ({ kind: 'appt' as const, t })),
      ]
    : [];
  const key = (kind: TriggerKind, value: number, remove: boolean) => `${kind}:${value}:${remove ? 1 : 0}`;
  const wantedKeys = new Set(wanted.map((w) => key(w.kind, w.value, w.remove)));
  const kept = new Set<string>();
  for (const { kind, t } of existing) {
    const k = key(kind, t.value, t.remove);
    if (wantedKeys.has(k) && !kept.has(k)) kept.add(k);
    else if (t.id) writes.push(apiClient.delete(`${TRIGGER_PATH[kind]}/${t.id}`));
  }
  for (const w of wanted) {
    if (kept.has(key(w.kind, w.value, w.remove))) continue;
    writes.push(apiClient.post(TRIGGER_PATH[w.kind], { taskGroupID, [TRIGGER_FIELD[w.kind]]: w.value, remove: w.remove }));
  }

  // Items.
  const before = new Map((original?.items ?? []).filter((i) => i.taskItemID).map((i) => [i.taskItemID, i]));
  const keptItems = new Set(d.items.filter((i) => i.taskItemID).map((i) => i.taskItemID));
  for (const id of before.keys()) if (!keptItems.has(id)) writes.push(apiClient.delete(`/task-items/${id}`));
  for (const item of d.items) {
    if (!item.taskItemID) writes.push(apiClient.post('/task-items', { groupID: taskGroupID, ...itemPayload(item) }));
    else if (itemChanged(item, before.get(item.taskItemID) ?? item)) writes.push(apiClient.put(`/task-items/${item.taskItemID}`, itemPayload(item)));
  }

  const results = await Promise.allSettled(writes);
  const failures = results
    .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
    .map((r) => getErrorMessage(r.reason, 'A change could not be saved.'));
  return { taskGroupID, failures: Array.from(new Set(failures)) };
}
